import { useCallback, useEffect, useState } from 'react'
import {
  Activity, AlertTriangle, CheckCircle2, Cpu, Database, HardDrive, Loader2,
  RefreshCw, Server, XCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, MetricCard, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { useCan } from '@/shared/auth/session'
import {
  checkIntegration, infrastructureReport, type InfrastructureReport,
} from './api'

/**
 * Is this deployment healthy?
 *
 * ## Every light is a real round trip
 *
 * A dashboard that reports "configured" as "up" is worse than none — it is confidently
 * wrong exactly when somebody is relying on it. Postgres gets a query, Redis gets a PING,
 * and both are timed, because a dependency answering in four seconds is technically
 * reachable and operationally broken.
 *
 * ## Sizes are a map of what this deployment holds
 *
 * Row counts per table say how many voters are registered and which counties are loaded.
 * In an election system that is not operational trivia, which is why the page sits behind
 * its own authority rather than a general read.
 */
export function InfrastructurePage() {
  const [data, setData] = useState<InfrastructureReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const mayAct = useCan('infrastructure:act') && useCan('integration:update')

  const load = useCallback(async () => {
    try {
      setError(null)
      setData(await infrastructureReport())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const recheck = async (code: string) => {
    setBusy(code)
    try {
      setError(null)
      setData(await checkIntegration(code))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const heapPct = data
    ? Math.round((data.jvm.heapUsedBytes / Math.max(1, data.jvm.heapMaxBytes)) * 100)
    : 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-erp-lg font-semibold">Infrastructure</h1>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            What is reachable, what the schema is at, what is growing, and what is failing.
          </p>
        </div>
        <button type="button" onClick={() => void load()}
          className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />
      {data === null && !error && <LoadingBlock />}

      {data && (
        <>
          <div className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <MetricCard label="Database" value={data.databasePretty} />
            <MetricCard label="Rows (estimate)" value={fmtNumber(data.totalRows)} />
            <MetricCard label="Schema version"
              value={data.migrations.currentVersion ?? '—'} />
            <MetricCard label="Failures (24h)"
              value={`${fmtNumber(data.errors.failuresLastDay)} · ${data.errors.failureRate}%`} />
          </div>

          {data.migrations.failed > 0 && (
            <Note tone="bad">
              {data.migrations.failed} migration(s) are recorded as FAILED. An application
              running against a schema older than its code presents as unrelated errors
              everywhere else — fix this before believing anything below.
            </Note>
          )}

          {/* ── services ──────────────────────────────────────────────── */}
          <div>
            <h2 className="text-erp-sm font-medium mb-1.5">Services</h2>
            <div className="grid gap-2"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              {data.services.map((s) => (
                <div key={s.name}
                  className={cn('rounded-md border px-3 py-2.5',
                    s.reachable ? 'border-border' : 'border-red-300 bg-red-50/50 dark:bg-red-500/10')}>
                  <div className="flex items-center gap-2">
                    {s.reachable
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      : <XCircle className="h-4 w-4 text-red-600 shrink-0" />}
                    <span className="text-erp-sm font-medium">{s.name}</span>
                    {s.latencyMs != null && (
                      <span className={cn('ml-auto text-erp-xs tabular-nums',
                        s.latencyMs > 1000
                          ? 'text-amber-700 dark:text-amber-400 font-medium'
                          : 'text-muted-foreground')}>
                        {s.latencyMs} ms
                      </span>
                    )}
                  </div>
                  <p className="text-erp-xs text-muted-foreground mt-1 truncate"
                    title={s.detail ?? ''}>
                    {s.detail ?? '—'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ── runtime + schema ──────────────────────────────────────── */}
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            <div className="rounded-md border border-border p-3">
              <h3 className="text-erp-sm font-medium mb-2 inline-flex items-center gap-1.5">
                <Cpu className="h-3.5 w-3.5" /> Runtime
              </h3>
              <dl className="text-erp-sm space-y-1">
                <Row label="Java" value={data.jvm.javaVersion} />
                <Row label="Uptime" value={data.jvm.uptime} />
                <Row label="Heap"
                  value={`${bytes(data.jvm.heapUsedBytes)} of ${bytes(data.jvm.heapMaxBytes)} (${heapPct}%)`} />
                <Row label="Threads" value={String(data.jvm.liveThreads)} />
                <Row label="CPUs" value={String(data.jvm.availableProcessors)} />
              </dl>
            </div>

            <div className="rounded-md border border-border p-3">
              <h3 className="text-erp-sm font-medium mb-2 inline-flex items-center gap-1.5">
                <Database className="h-3.5 w-3.5" /> Schema
              </h3>
              <dl className="text-erp-sm space-y-1">
                <Row label="Version" value={data.migrations.currentVersion ?? '—'} />
                <Row label="Applied" value={String(data.migrations.applied)} />
                <Row label="Failed" value={String(data.migrations.failed)} />
                <Row label="Last"
                  value={data.migrations.lastAppliedAt
                    ? `${fmtDateTime(data.migrations.lastAppliedAt)} — ${data.migrations.lastDescription ?? ''}`
                    : '—'} />
              </dl>
            </div>
          </div>

          {/* ── integrations ──────────────────────────────────────────── */}
          <div>
            <h2 className="text-erp-sm font-medium mb-1.5 inline-flex items-center gap-1.5">
              <Server className="h-3.5 w-3.5" /> Integrations
            </h2>
            <ErpTable head={(
              <>
                <Th>Integration</Th>
                <Th>Category</Th>
                <Th>Status</Th>
                <Th>Health</Th>
                <Th>Checked</Th>
                <Th />
              </>
            )}>
              {data.integrations.map((i) => (
                <tr key={i.code}>
                  <Td>
                    {i.code}
                    <span className="block text-erp-xs text-muted-foreground">{i.provider}</span>
                  </Td>
                  <Td className="text-erp-xs">{i.category}</Td>
                  <Td>
                    <span className={cn('text-erp-xs',
                      i.status === 'ACTIVE'
                        ? 'text-emerald-700 dark:text-emerald-400'
                        : 'text-muted-foreground')}>
                      {i.status.toLowerCase()}
                    </span>
                  </Td>
                  <Td>
                    <span className={cn('text-erp-xs',
                      i.health === 'UP' && 'text-emerald-700 dark:text-emerald-400',
                      i.health === 'DOWN' && 'text-red-700 dark:text-red-400')}>
                      {i.health?.toLowerCase() ?? '—'}
                    </span>
                    {i.lastError && (
                      <span className="block text-erp-xs text-muted-foreground truncate max-w-[18rem]"
                        title={i.lastError}>{i.lastError}</span>
                    )}
                  </Td>
                  <Td className="text-erp-xs text-muted-foreground">
                    {i.lastCheckedAt ? fmtDateTime(i.lastCheckedAt) : 'never'}
                  </Td>
                  <Td className="text-right">
                    {mayAct && (
                      <button type="button" disabled={busy !== null}
                        onClick={() => void recheck(i.code)}
                        className="inline-flex items-center gap-1 text-erp-xs text-chocolate
                                   hover:underline">
                        {busy === i.code ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Activity className="h-3 w-3" />}
                        Check now
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </ErpTable>
            {!mayAct && (
              <p className="text-erp-xs text-muted-foreground mt-1">
                Re-checking needs both <code>infrastructure:act</code> and
                {' '}<code>integration:update</code> — the second because that is what guards
                this action everywhere else.
              </p>
            )}
          </div>

          {/* ── largest tables ────────────────────────────────────────── */}
          <div>
            <h2 className="text-erp-sm font-medium mb-1.5 inline-flex items-center gap-1.5">
              <HardDrive className="h-3.5 w-3.5" /> Largest tables
            </h2>
            <ErpTable head={(
              <><Th>Table</Th><Th className="text-right">Rows</Th><Th className="text-right">Size</Th></>
            )}>
              {data.largestTables.map((t) => (
                <tr key={t.table}>
                  <Td className="font-mono text-erp-xs">{t.table}</Td>
                  <Td className="text-right tabular-nums">{fmtNumber(t.rows)}</Td>
                  <Td className="text-right tabular-nums">{t.pretty}</Td>
                </tr>
              ))}
            </ErpTable>
            <p className="text-erp-xs text-muted-foreground mt-1">
              Row counts are the planner's estimate — an exact count would scan every table
              in the database to draw this page. Size includes indexes, because an index
              larger than its table is a real problem a heap-only figure hides.
            </p>
          </div>

          {/* ── failures ──────────────────────────────────────────────── */}
          <div>
            <h2 className="text-erp-sm font-medium mb-1.5 inline-flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> Recent failures
            </h2>
            {data.errors.recent.length === 0 ? (
              <p className="text-erp-xs text-muted-foreground">
                Nothing has failed recently — {fmtNumber(data.errors.eventsLastDay)} audited
                events in the last day, none of them a failure.
              </p>
            ) : (
              <ErpTable head={(<><Th>Action</Th><Th>Category</Th><Th>Outcome</Th><Th>When</Th></>)}>
                {data.errors.recent.map((f, i) => (
                  <tr key={`${f.action}-${i}`}>
                    <Td className="font-mono text-erp-xs">{f.action}</Td>
                    <Td className="text-erp-xs">{f.category}</Td>
                    <Td className="text-erp-xs text-red-700 dark:text-red-400">{f.outcome}</Td>
                    <Td className="text-erp-xs text-muted-foreground">{fmtDateTime(f.at)}</Td>
                  </tr>
                ))}
              </ErpTable>
            )}
            <p className="text-erp-xs text-muted-foreground mt-1">
              From the audit trail, not a log file — it survives a restart and is the same in
              every environment.
            </p>
          </div>
        </>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-20 shrink-0 text-erp-xs text-muted-foreground pt-0.5">{label}</dt>
      <dd className="flex-1 text-erp-sm">{value}</dd>
    </div>
  )
}

function bytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(0)} MB`
  return `${(n / 1024 ** 3).toFixed(1)} GB`
}
